/* ------------------------------------------------------------------
   bioContact.js  —  the Link in Bio contact button (Part E, T32)
   The same rules as backend/utils/bioExtras.js, so the editor can say
   what's wrong before saving. The server checks again and is the one
   that counts. Also: the address the QR code encodes.
------------------------------------------------------------------ */
import React from 'react';
import { SITE_URL } from '../../constants';

export const CONTACT_TYPES = [
  { key: 'email', label: 'Email', field: 'Email address',     placeholder: 'you@example.com',         icon: 'fa-solid fa-envelope' },
  { key: 'call',  label: 'Call',  field: 'Phone number',      placeholder: '+1 503 555 0123',         icon: 'fa-solid fa-phone' },
  { key: 'text',  label: 'Text',  field: 'Phone number',      placeholder: '+1 503 555 0123',         icon: 'fa-solid fa-comment-sms' },
  { key: 'book',  label: 'Book',  field: 'Booking page link', placeholder: 'https://calendly.com/you', icon: 'fa-solid fa-calendar-check' },
];
export const DEFAULT_LABELS = { email: 'Email me', call: 'Call me', text: 'Text me', book: 'Book now' };
export const LABEL_MAX = 60;
export const EMPTY_CONTACT = { enabled: false, type: 'email', value: '', label: '' };

const EMAIL_RE = /^[^\s@<>()[\]",;:\\]+@[^\s@<>()[\]",;:\\]+\.[A-Za-z]{2,}$/;

export function cleanEmail(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length <= 254 && EMAIL_RE.test(s) ? s : null;
}
export function cleanPhone(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s || s.length > 30 || !/^\+?[\d\s().-]+$/.test(s)) return null;
  const digits = s.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return (s.startsWith('+') ? '+' : '') + digits;
}
function cleanWebUrl(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s || s.length > 500 || !/^https?:\/\//i.test(s)) return null;
  try { return new URL(s).hostname.includes('.') ? s : null; } catch { return null; }
}
export function cleanContactValue(type, value) {
  if (type === 'email') return cleanEmail(value);
  if (type === 'call' || type === 'text') return cleanPhone(value);
  if (type === 'book') return cleanWebUrl(value);
  return null;
}

const ERRORS = {
  email: 'Enter a valid email address, like you@example.com.',
  call: 'Enter a valid phone number: 7 to 15 digits, for example +1 503 555 0123.',
  text: 'Enter a valid phone number: 7 to 15 digits, for example +1 503 555 0123.',
  book: 'Enter your booking page address, starting with https://',
};

/** null when fine to save, else the message to show. */
export function contactError(c) {
  if (!c) return null;
  const value = (c.value || '').trim();
  if (!value) return c.enabled ? ERRORS[c.type] : null;
  if (!cleanContactValue(c.type, value)) return ERRORS[c.type];
  if ((c.label || '').trim().length > LABEL_MAX) return `Button text can be up to ${LABEL_MAX} characters.`;
  return null;
}

export function contactHref(type, value) {
  const v = cleanContactValue(type, value);
  if (!v) return null;
  if (type === 'email') return `mailto:${v}`;
  if (type === 'call') return `tel:${v}`;
  if (type === 'text') return `sms:${v}`;
  return v;
}

/** What the public page shows, from the editor's state (null = nothing). */
export function previewContact(c) {
  if (!c || !c.enabled) return null;
  const href = contactHref(c.type, c.value);
  if (!href) return null;
  return { type: c.type, label: (c.label || '').trim() || DEFAULT_LABELS[c.type], href };
}

/** The address the QR code encodes: the live page, tagged so analytics
    can count QR visits (backend/routes/bio.js › ?src=qr). */
export function qrPageUrl(username, origin = SITE_URL) {
  return `${origin.replace(/\/+$/, '')}/u/${encodeURIComponent(username || '')}?src=qr`;
}

/* The big button at the top of the page (public page + live preview).
   Solid in the theme's text colour so it stands apart from the links. */
export function ContactButton({ contact, css, preview = false, compact = false }) {
  if (!contact || !contact.label || !/^(mailto:|tel:|sms:|https?:\/\/)/i.test(String(contact.href || ''))) return null;
  const type = CONTACT_TYPES.find(t => t.key === contact.type);
  const style = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%', boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none',
    padding: compact ? '12px' : '15px 18px', borderRadius: css.button?.borderRadius ?? 12, fontWeight: 700, fontSize: compact ? 14 : 16,
    background: css.textColor, color: pickOn(css.textColor),
  };
  const body = (
    <>
      {type && <i className={type.icon} aria-hidden="true" />}
      <span>{contact.label}</span>
    </>
  );
  if (preview) return <div data-testid="contact-button" style={style}>{body}</div>;
  return (
    <a data-testid="contact-button" href={contact.href}
      {...(contact.type === 'book' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={style}>
      {body}
    </a>
  );
}

/* white or near-black text, whichever reads better on `hex` */
function pickOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#111827' : '#ffffff';
}
